import React, { useEffect } from 'react';
import { ImportButton } from 'components/ui/ImportButton/ImportButton';
import PapaParse, { ParseConfig } from 'papaparse';
import { notify } from 'services/NotificationService';
import { propNamesToForceParseAsString } from 'interfaces/ICSV';
import { useInjection } from 'services/ioc/ioc.react';
import { CustomerCSVConfigurationStore } from 'stores/CustomerCSVConfigurationStore';
import { ServiceTypes } from 'constants/ServiceTypes';
import { observer } from 'mobx-react-lite';

interface IProps {
  onFileUpload: (file: any) => void;
  disabled: boolean;
}

export const ImportCSV: React.FC<IProps> = observer(({ onFileUpload, disabled }) => {
  let fileReader: FileReader;
  const customerCSVConfigurationStore = useInjection<CustomerCSVConfigurationStore>(ServiceTypes.CustomerCSVConfigurationStore);

  useEffect(() => {
    customerCSVConfigurationStore.getConfiguration();
  }, []);

  const transformHeader = (header: string) => header.toLowerCase().replace(/\W/g, '');
  const parseOptions = {
    header: true,
    dynamicTyping: (header: string) => !propNamesToForceParseAsString.some((p) => header === p),
    skipEmptyLines: true,
    transformHeader,
  } as ParseConfig;

  const handleFileRead = () => {
    let content = fileReader.result as string;

    if (content.length === 0) {
      notify('Empty file', 'Please upload a non-empty CSV file.', 'error');
      return;
    }

    const rows = PapaParse.parse(content, Object.assign(parseOptions)).data;

    if (rows.length === 0) {
      notify('No rows', 'Please upload a CSV file with valid data.', 'error');
      return;
    }

    content = content.split('\u0000').join('');

    const headers = PapaParse.parse(content, Object.assign({ ...parseOptions }, { preview: 1 })).meta.fields;

    const missingHeaders = customerCSVConfigurationStore.defaultColumns.filter(
      (header) => !headers.some((h) => h.toLowerCase().replace(/\//g, '') === header.toLowerCase().replace(/\//g, ''))
    );

    if (missingHeaders.length > 0) {
      notify('Missing headers', `Missing required header(s)`, 'error');
      return;
    }

    const csvData = PapaParse.parse(content, Object.assign({ ...parseOptions }, { error: handleError, encoding: 'UTF-8' }));
    onFileUpload(csvData.data);
  };

  const handleError = () => {
    notify('Error', 'An error occured please try again', 'error');
  };

  const isValidExtension = (fileName: string) => {
    return fileName.includes('.csv') && fileName.split('.').pop() === 'csv';
  };

  const handleOnChange = (file: any) => {
    if (!isValidExtension(file.name)) {
      notify('Wrong file format', 'Please upload a valid CSV file.', 'error');
      return;
    }

    fileReader = new FileReader();
    fileReader.onerror = handleError;
    fileReader.onloadend = handleFileRead;
    fileReader.readAsText(file);
  };

  return (
    <ImportButton label='import cleaned csv' onChange={(e) => handleOnChange(e.target.files && e.target.files[0])} disabled={disabled} />
  );
});
